import type { Schema } from '~~/common/types/api';
import type { CategoryType } from './category';
import { getCategoryDisplayName } from './category';
import { getBoutiqueUrl } from '~~/common/composables/useUrls';
import type { FilterValue } from '~~/common/stores/products';
import type { PostTranslationPluginProvides } from '~~/common/plugins/post-translation';

export const RESOURCE_TYPE_REDIRECT = 'redirect';

type UrlResourceType = Schema<'UrlModel'>;

const resources = [
  {
    id: 'product',
    check (p: string) {
      return p.matchAll(/^.*?\.p[-]?([0-9]+)\/([0-9]+)\.v[-]?([0-9]+)(\?|#|$)/gi).next().value?.[2] || // get SKU number, if URL contain .p and .v and SKU
          p.matchAll(/^.*?\.p[-]?([0-9]+)(\?|#|$)/gi).next().value?.[1] // get p. as SKU number
      ;
    },
  },
  {
    id: 'boutique',
    check (p: string) {
      return (p.matchAll(/.*?\.b[-]?([0-9]+).*?\.c[-]?([0-9]+)/gi).next().value || []).splice(1).join('|');
    },
  },
  {
    id: 'category',
    check (p: string) {
      return p.matchAll(/.*?\.c[-]?([0-9]+)/gi).next().value?.[1];
    },
  },
  {
    id: 'search',
    check (p: string) {
      const q = p.matchAll(/.*?\/search\?(.*?)((?=#)|$)/gi).next().value?.[1];
      if (q) {
        const p1 = new URLSearchParams(q);
        return 'q[]=' + p1.getAll('q[]').join('&q[]=');
      }
    },
  },
  {
    id: 'compare',
    check (p: string) {
      const path = p.split('#')?.[0]?.split('?')[0];
      return path?.endsWith('/compare') || path?.endsWith('/termek-osszehasonlitas') ? 'cmp' : false;
    },
  },
  {
    id: 'shortlist',
    check (p: string) {
      return p.matchAll(/.*?\/list\/([^?#]*)/gi).next().value?.[1];
    },
  },
  {
    id: 'sds',
    check (p: string) {
      const path = p.split('#')?.[0]?.split('?')[0];
      return path?.endsWith('/safety-data-sheet') || path?.endsWith('/biztonsagi-adatlapok') ? 'sds' : false;
    },
  },
  {
    id: 'giftCardRequest',
    check (p: string) {
      const path = p.split('#')?.[0]?.split('?')[0];
      return path?.endsWith('/ajandekkartya-igenyles') ? 'giftCardRequest' : false;
    },
  },
  {
    id: 'csrSeoResource',
    check (p: string) {
      const path = p.split('#')?.[0]?.split('?')[0];
      return path?.endsWith('/csr') ? 'csrSeoResource' : false;
    },
  },
  {
    id: 'loyaltyFaq',
    check (p: string) {
      const path = p.split('#')?.[0]?.split('?')[0];
      return path?.endsWith('/loyalty-program/faq') || path?.endsWith('/bizalomkartya/gyik') ? 'loyaltyFaq' : false;
    },
  },
  {
    id: 'petrolFaq',
    check (p: string) {
      const path = p.split('#')?.[0]?.split('?')[0];
      return path?.endsWith('/petrol/faq') || path?.endsWith('/benzinkutak/gyik') ? 'petrolFaq' : false;
    },
  },
  {
    id: 'faq',
    check (p: string) {
      const path = p.split('#')?.[0]?.split('?')[0];
      return path?.endsWith('/faq') || path?.endsWith('/gyik') ? 'faq' : false;
    },
  },
  {
    id: 'foodDeliveryMap',
    check (p: string) {
      const path = p.split('#')?.[0]?.split('?')?.[0];
      return path?.endsWith('/food-delivery-map') || path?.endsWith('/kiszallitasi-teruleteink') ? 'foodDeliveryMap' : false;
    },
  },
  {
    id: 'inspirations',
    check (p: string) {
      const path = p.split('#')?.[0]?.split('?')?.[0];
      return path?.endsWith('/inspirations') || path?.endsWith('/inspiraciok') ? 'inspirations' : false;
    },
  },
  {
    id: 'careerNews',
    check (p: string) {
      const path = p.split('#')?.[0]?.split('?')?.[0];
      return path?.endsWith('/karrier/hirek') ? 'careerNews' : false;
    },
  },
  {
    id: 'news',
    check (p: string) {
      const path = p.split('#')?.[0]?.split('?')?.[0];
      return path?.endsWith('/news') || path?.endsWith('/hirek') ? 'news' : false;
    },
  },
  {
    id: 'press',
    check (p: string) {
      const path = p.split('#')?.[0]?.split('?')?.[0];
      return path?.endsWith('/press') || path?.endsWith('/sajtokozlemenyek') ? 'press' : false;
    },
  },
  {
    id: 'prizeGame',
    check (p: string) {
      const path = p.split('#')?.[0]?.split('?')?.[0];
      return path?.endsWith('/prize-game') || path?.endsWith('/nyeremenyjatekok') ? 'prizeGame' : false;
    },
  },
  {
    id: 'unsubscribe',
    check (p: string) {
      const path = p.split('#')?.[0]?.split('?')?.[0];
      return path?.endsWith('/unsubscribe') || path?.endsWith('/hirlevel-leiratkozas') ? 'unsubscribe' : false;
    },
  },
  {
    id: 'careerJob',
    check (p: string) {
      const path = p.split('#')?.[0]?.split('?')?.[0];
      return path === '/karrier' || path === '/en/karrier' ? 'careerJob' : false;
    },
  },
  {
    id: 'basket',
    check (p: string) {
      const path = p.split('#')?.[0]?.split('?')?.[0];
      return path?.endsWith('/shop/basket') || path?.endsWith('/shop/kosar') ? 'basket' : false;
    },
  },
  {
    id: 'catalog',
    check (p: string) {
      const path = p.split('#')?.[0]?.split('?')?.[0];
      return path?.endsWith('/catalog') || path?.endsWith('/katalogusok') ? 'catalog' : false;
    },
  },
  {
    id: 'store',
    check (p: string) {
      return p.matchAll(/(aruhazak|stores)(\/[^?#]*|)?$/gi).next().value?.[1];
    },
  },
  {
    id: 'petrol',
    check (p: string) {
      // Only a station URL (`/benzinkutak/<slug>`, trailing slash optional) is a
      // petrol resource. The bare `/benzinkutak` is a CMS landing page served by
      // the [...slug] catch-all, so it must fall through to the url-resource
      // lookup (`cms_content`).
      return p.matchAll(/(benzinkutak|petrol)\/[^?#/]+\/?$/gi).next().value?.[1];
    },
  },
];

export function extractResourceFromPath (path: string): { resourceType: string; resourceId: string } | undefined {
  for (const res of resources) {
    const resourceId = res.check(path);
    if (resourceId) {
      return {
        resourceType: res.id,
        resourceId,
      };
    }
  }
}

export function getSubcategoryId ({ href }: { href: string }): FilterValue | undefined {
  // URLSearchParams.get() percent-decodes once, which leaves plain JSON. Links that
  // carry an extra encoded layer need one more decode before they parse.
  const qq = new URLSearchParams(href.split('?').slice(1).join('')).get('qq') ?? '';
  if (!qq) {
    return undefined;
  }
  for (const attempt of [qq, decodeURIComponent(qq)]) {
    try {
      const parsed: unknown = JSON.parse(attempt);
      if (typeof parsed === 'object' && parsed !== null && 'filterParams' in parsed) {
        const { filterParams } = parsed;
        if (typeof filterParams === 'object' && filterParams !== null && 'subcategory' in filterParams) {
          const { subcategory } = filterParams;
          // `qq` is user-controlled, and Array.isArray() narrows unknown to any[], so the
          // element needs a scalar check of its own before it can be handed to a caller.
          const firstSubcategory: unknown = Array.isArray(subcategory) ? subcategory[0] : undefined;
          if (typeof firstSubcategory === 'string' || typeof firstSubcategory === 'number') {
            return firstSubcategory;
          }
          return undefined;
        }
      }
      return undefined;
    } catch {
      // Try the next form.
    }
  }
  return undefined;
}

export function resetPath (path: string | undefined, urlPrefix: string) {
  return path?.replace(urlPrefix, '').replace(/[/]+$/, '').replace(/^[/]+/, '') ?? '';
}

// For a `catalog/<slug>` (or `katalogusok/<slug>`) path, return the bare `<slug>`.
// The `/url-resource` endpoint resolves the bare slug and returns null for the
// prefixed one, so both the catch-all page and the routeHistory middleware must
// strip the prefix before looking a catalog URL up.
const CATALOG_SLUG_REGEX = /^(catalog|katalogusok)\/([^?/]+)/;

export function extractCatalogSlug (resetPathValue: string): string | undefined {
  return resetPathValue.match(CATALOG_SLUG_REGEX)?.[2];
}

/**
 * Root (2nd) breadcrumb level for a CMS page, keyed by its first `contentRootTypes` value.
 * Only the types listed here have a listing page to link to; every other type
 * (`homepage`, `dh_homepage`, `product`, `product_list`, `shortlist`, …) gets no
 * root level at all, because the generated URL would lead to a 404.
 */
const CMS_BREADCRUMB_ROOT_LEVEL_MAP: Record<string, { to: string, token: string }> = {
  news: { to: '/news', token: 'news' },
  csr: { to: '/csr', token: 'csr' },
  inspiration: { to: 'inspirations', token: 'inspiration' },
  career: { to: '/karrier', token: 'career' },
  career_news: { to: '/karrier/hirek', token: 'career.news' },
  press_release: { to: 'press', token: 'press' },
  prize_draw: { to: 'prize-game', token: 'prize_draw' },
  // The petrol landing is a CMS page with no static route, so its URL is
  // not knowable here. AwPetrolMenu fills the link in from the CMS
  // petrol-menu; until then the level renders as plain text.
  petrol: { to: '', token: 'petrol' },
};

export function loadBreadcrumb ({
  urlResource,
  category,
  productCategories,
  product,
  cmsToken,
  urlPrefix,
  contentRootTypes,
  $awt,
}: {
  urlResource: UrlResourceType;
  category: CategoryType;
  productCategories: CategoryType[];
  product: {
    token: string;
    to: string;
  };
  cmsToken: string;
  urlPrefix: string;
  contentRootTypes: string[];
  $awt: PostTranslationPluginProvides['awt'];
}) {
  return ({
    category () {
      return (category?.parentsNameArr || []).map(item => ({
        token: getCategoryDisplayName(item),
        to: `${urlPrefix}/${item.slug}.c-${item.categoryId}`,
        categoryId: item.categoryId,
      }));
    },
    boutique () {
      return (category?.parentsNameArr || []).map(item => ({
        token: getCategoryDisplayName(item),
        to: `${urlPrefix}/${item.slug}`,
      }));
    },
    product () {
      let tmpSlug = '';
      return (productCategories
        .map((c) => {
          const boutique = c.boutique ? getBoutiqueUrl(c) : '';
          tmpSlug = (tmpSlug ? tmpSlug + '/' : '') + c.slug?.split('_')[0];
          return {
            token: getCategoryDisplayName(c),
            to: `${urlPrefix}/${boutique}${tmpSlug}.c-${c.id}`,
          };
        })
        .concat(product)
      );
    },
    cms_content () {
      const result = [];

      const rootLevel = contentRootTypes?.[0] ? CMS_BREADCRUMB_ROOT_LEVEL_MAP[contentRootTypes[0]] : undefined;
      if (rootLevel) {
        const { to, token } = rootLevel;

        result.push({
          token: $awt(`aw.pages.${token}.title`),
          to: to,
          disabled: !to,
        });
      }

      return [...result, {
        token: cmsToken,
        to: `/${urlResource.slug}`,
      }];
    },
  })[urlResource?.resourceType];
}
